import {
  useContext, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { AddModelPortfoliosIPSAndAssetClassesVisual, AssetClassTargetAllocation } from './addModelPortfoliosIPSAndAssetClasses.visual';
import { WorkflowContext } from '../../workflowCompletion';
import { TranslatedString } from '../../../../../interfaces/shared';
import { useSaveModelPortfolio } from '../utils/useSaveModelPortfolio';
import { Security } from '../../../../3-pattern';
import { Branch } from '../../../../../interfaces';
import { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio';

export interface AddModelPortfoliosIPSAndAssetClassesOptions {
  title: TranslatedString;
  ips: {
    enabled: boolean;
    label: TranslatedString;
    options: {
      description: {
        enabled: boolean;
        label: TranslatedString;
        required: 'ERROR' | 'NOT_REQUIRED';
      };
      objective: {
        enabled: boolean;
        label: TranslatedString;
        required: 'ERROR' | 'NOT_REQUIRED';
      };
      liquidity: {
        enabled: boolean;
        label: TranslatedString;
        required: 'ERROR' | 'NOT_REQUIRED';
      };
    };
  };
  disclaimer: {
    enabled: boolean;
    label: TranslatedString;
    required: 'ERROR' | 'NOT_REQUIRED';
  };
  assetClassesDistribution: {
    enabled: boolean;
    label: TranslatedString;
    required: 'ERROR' | 'NOT_REQUIRED';
  };
  buttonSaveForLater: {
    enabled: boolean;
    label: TranslatedString;
  };
}

interface AddModelPortfoliosIPSAndAssetClassesProps {
  options: AddModelPortfoliosIPSAndAssetClassesOptions;
  workflowCompletion: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
}

export const AddModelPortfoliosIPSAndAssetClasses = ({
  options,
  workflowCompletion,
  userId,
  onNext,
  stepLoading,
}: AddModelPortfoliosIPSAndAssetClassesProps) => {
  const { workflowData, setWorkflowData, updateWorkflowContext } = useContext(WorkflowContext);
  const securities: Security[] = useMemo(() => workflowData.modelPortfolioDetails?.securities || [], [workflowData.modelPortfolioDetails?.securities]);

  const [modelPortfolioData, setModelPortfolioData] = useState<ModelPortfolioData>(() => {
    const existingAllocations = workflowData.modelPortfolioDetails?.targetAllocations || [];
    const existingAssetClassIds = new Set(existingAllocations.map((allocation: AssetClassTargetAllocation) => allocation.secondaryAssetClass.id));

    if (existingAllocations.length) {
      return {
        ...workflowData.modelPortfolioDetails,
        targetAllocations: [...existingAllocations],
      };
    }

    const autoAllocations = securities?.length ? securities.reduce((acc: { [key: string]: Branch[] }, security: Branch) => {
      if (security.financialProduct?.isCash) {
        return acc;
      }

      const groupBy = workflowData.modelPortfolioDetails?.groupBy || 'primary';
      const assetClassId = groupBy === 'primary'
        ? security.financialProduct?.primaryAssetClass?.id
        : security.financialProduct?.secondaryAssetClass?.id;

      if (assetClassId && !existingAssetClassIds.has(assetClassId)) {
        if (!acc[assetClassId]) {
          acc[assetClassId] = [];
        }
        acc[assetClassId].push(security);
      }
      return acc;
    }, {}) : {};

    const newAutoAllocations = Object.entries(autoAllocations)
      .map(([assetClassId, securitiesToGroup]) => ({
        secondaryAssetClass: { id: assetClassId },
        actualPercentage: securitiesToGroup.reduce((sum, security) => sum + security.percentage, 0),
        min: 0,
        max: 100,
        color: 'supportOne' as const,
        fromSecurity: true,
        securityPrimaryAssetClassId: securitiesToGroup[0].financialProduct?.primaryAssetClass?.id,
        securitySecondaryAssetClassId: securitiesToGroup[0].financialProduct?.secondaryAssetClass?.id,
      }));

    return {
      ...workflowData.modelPortfolioDetails,
      targetAllocations: [...existingAllocations, ...newAutoAllocations],
    };
  });

  const { saveModelPortfolio, loading } = useSaveModelPortfolio();
  const navigate = useNavigate();

  const handleUpdateModelPortfolioData = async (data: ModelPortfolioData) => {
    setModelPortfolioData({ ...modelPortfolioData, ...data });
    setWorkflowData({ ...workflowData, modelPortfolioDetails: { ...modelPortfolioData, ...data } });

    if (updateWorkflowContext) {
      await updateWorkflowContext();
    }
  };

  const handleSaveForLater = async () => {
    try {
      const savedModelPortfolioData = await saveModelPortfolio(modelPortfolioData);
      const savedModel = {
        ...savedModelPortfolioData,
        originalValues: savedModelPortfolioData,
      };
      setModelPortfolioData(savedModel);
      setWorkflowData({
        ...workflowData,
        modelPortfolioDetails: savedModel,
      });
    } catch (error) {
      // Error is already handled by saveModelPortfolio
    }
  };

  const handleCreateModelPortfolio = async () => {
    try {
      const savedModelPortfolioData = await saveModelPortfolio(modelPortfolioData, true);

      onNext();

      if (savedModelPortfolioData?.id) {
        navigate(`/modelPortfolios/${savedModelPortfolioData.id}`);
      }
    } catch (error) {
      // Error is already handled by saveModelPortfolio
    }
  };

  return (
    <AddModelPortfoliosIPSAndAssetClassesVisual
      options={options}
      workflowCompletion={workflowCompletion}
      stepLoading={stepLoading}
      modelPortfolioData={modelPortfolioData}
      setModelPortfolioData={handleUpdateModelPortfolioData}
      onSaveForLater={handleSaveForLater}
      onCreateModelPortfolio={handleCreateModelPortfolio}
      loading={loading}
    />
  );
};
